/**
 * Folders view: a signed container (.asice/.bdoc/.ddoc) is a FILE, not a folder.
 *
 * Renders it as a single signed-container row — seal icon, real extension, and
 * the SAME meta line every other file in the list shows — whose title click
 * opens the shared signature panel instead of navigating into a folder. The
 * chevron expands a dropdown of the enclosed documents rather than a folder
 * level. Everything container-specific (documents, signatures, the ASiC-E
 * label) lives in that panel, not inline.
 *
 * Plain `.zip` archives keep the folder-like `ContainerFolderRow`.
 */

import { Loader2 } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { DocumentActions } from "../molecules/DocumentActions";
import { DocumentCheckbox } from "../molecules/DocumentCheckbox";
import { EnclosedDocumentsMenu } from "../molecules/EnclosedDocumentsMenu";
import { DocumentRowMeta } from "../atoms/DocumentRowMeta";
import { useDocumentRowDecorations } from "./documentRowDecorations";
import { SignedFileIcon } from "../atoms/SignedContainerIndicators";
import { summariseContainerLookup, type SignatureStatus } from "../../signing/signatureModel";
import { useSignedContainerInfoQuery } from "../../signing/useSignedContainerInfo";
import { useSignaturePanel } from "../../signing/signaturePanelContext";
import { isDocumentProcessing } from "../../utils/documentState";
import { rowSelectionToggle } from "../../utils/rowSelectionClick";

/** The container document itself. */
interface SignedContainerDoc {
  /** Container document id. */
  id: string;
  /** Container filename, extension included (e.g. "contract-2026-07-24.asice"). */
  filename: string;
  /** Container size in bytes. */
  size?: number | null;
  /** Upload timestamp — the meta line renders it exactly as document rows do. */
  createdAt: string;
  /** Uploader display name, as shown on every other row in the list. */
  createdByName?: string | null;
  /** Document processing state. */
  state: string | null;
}

interface SignedContainerSelection {
  isSelected?: boolean;
  showCheckbox?: boolean;
  onSelect?: (isSelected: boolean) => void;
}

interface SignedContainerActions {
  showActions?: boolean;
  onDownload?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action (trash view). */
  isRestore?: boolean;
}

interface SignedContainerRowProps {
  document: SignedContainerDoc;
  selection?: SignedContainerSelection;
  actions?: SignedContainerActions;
  /** Mirrors the list's Uploader column visibility. */
  showUser?: boolean;
  className?: string;
}

const SELECTION_DEFAULTS = { isSelected: false, showCheckbox: true } as const;

// Checkbox column width must match DocumentTableRow / DocumentListHeader so the
// checkboxes line up vertically across container and document rows.
function gridTemplate(showCheckbox: boolean, showActions: boolean): string {
  return [showCheckbox && "28px", "1fr", showActions && "50px"].filter(Boolean).join(" ");
}

export function SignedContainerRow({
  document,
  selection,
  actions,
  showUser = true,
  className,
}: SignedContainerRowProps) {
  const { isSelected, showCheckbox, onSelect } = { ...SELECTION_DEFAULTS, ...selection };
  const showActions = actions?.showActions !== false;

  return (
    <div
      className={cn(
        "group grid gap-4 border-b px-6 py-4 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50",
        className
      )}
      style={{ gridTemplateColumns: gridTemplate(showCheckbox, showActions) }}
      onClick={rowSelectionToggle(isSelected, onSelect)}
    >
      {showCheckbox && (
        <DocumentCheckbox isSelected={isSelected} onSelect={(selected) => onSelect?.(selected)} />
      )}

      <ContainerSummary
        document={document}
        showUser={showUser}
        isProcessing={document.state !== null && isDocumentProcessing(document.state)}
      />

      {showActions && <RowActions actions={actions} />}
    </div>
  );
}

// DocumentActions takes both handlers; `showDownload`/`showDelete` hide the ones
// a given row doesn't wire up.
const noop = () => undefined;
const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

function RowActions({ actions = {} }: { actions?: SignedContainerActions }) {
  const { onDownload, onDelete, deleteLabel, isRestore } = actions;

  return (
    <div className="flex justify-end" data-actions onClick={stopRowClick}>
      <div className="opacity-0 transition-opacity group-hover:opacity-100">
        <DocumentActions
          onDownload={onDownload || noop}
          onDelete={onDelete || noop}
          showDownload={Boolean(onDownload)}
          showDelete={Boolean(onDelete)}
          deleteLabel={deleteLabel}
          isRestore={isRestore}
        />
      </div>
    </div>
  );
}

/** The seal always draws; an unresolved lookup gets the neutral seal, not a claim. */
function iconStatus(status: SignatureStatus | null): SignatureStatus {
  return status ?? "unverified";
}

/** The name cell: seal icon, title row, and the standard document meta line. */
function ContainerSummary({
  document,
  showUser,
  isProcessing,
}: {
  document: SignedContainerDoc;
  showUser: boolean;
  isProcessing: boolean;
}) {
  const { id, filename } = document;
  const infoQuery = useSignedContainerInfoQuery(id);
  const { status } = summariseContainerLookup(infoQuery.isSuccess, infoQuery.data);
  const singleLineRows = useDocumentRowDecorations()?.singleLineRows ?? false;

  return (
    <div className="flex min-w-0 gap-3">
      <div className="flex-shrink-0 pt-1">
        <SignedFileIcon filename={filename} status={iconStatus(status)} />
      </div>
      <div className="min-w-0 flex-1">
        <ContainerTitleRow id={id} filename={filename} />
        {isProcessing ? (
          <ExtractingMeta singleLine={singleLineRows} />
        ) : (
          <DocumentRowMeta
            size={document.size}
            createdAt={document.createdAt}
            createdByName={document.createdByName}
            showUser={showUser}
            // A host that keeps rows one height (the Brief) means this row too.
            singleLine={singleLineRows}
          />
        )}
      </div>
    </div>
  );
}

/** Filename (opens the panel) and the enclosed-documents chevron. The signature
 *  status is carried by the seal on the file icon alone — the lists show no
 *  written status. */
function ContainerTitleRow({ id, filename }: { id: string; filename: string }) {
  const panel = useSignaturePanel();
  const openPanel = () => panel?.open({ containerId: id });

  const handleTitleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.stopPropagation();
    openPanel();
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-row-title
        onClick={handleTitleClick}
        disabled={!panel}
        className={cn(
          "max-w-full truncate text-left text-sm font-medium",
          panel && "cursor-pointer hover:text-primary hover:underline"
        )}
      >
        {filename}
      </button>
      <EnclosedDocumentsMenu containerId={id} containerFilename={filename} />
    </div>
  );
}

/** While the container is still being unzipped there is no meta to show yet.
 *  On a one-line host it keeps to one line, ellipsised, like the meta it
 *  stands in for. */
function ExtractingMeta({ singleLine = false }: { singleLine?: boolean }) {
  const { t } = useLanguage();

  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
      <Loader2 className={cn("h-3 w-3 animate-spin", singleLine && "flex-shrink-0")} />
      <span className={cn(singleLine && "min-w-0 truncate")}>
        {t.documents.signedContainer.extracting}
      </span>
    </div>
  );
}
